import { useMemo } from 'react';

import { FlexColumn, Gap } from 'ui-components/components/flex';

import { ReturnDetails } from '../components/return-details';
import { CustomerAddress } from '../components/customer-address';
import { RmaInfo } from '../components/rma-info';
import { CostSummary } from '../components/cost-summary';
import { LineItems } from '../components/line-items';
import { ExchangeItem } from '../components/exchange-item';
import { LineItemActions } from '../components/line-item-actions';
import { useReturnDataContext } from '../return-data-provider';
import { isAwaitingShipment } from '../../../../store/returns/returns.helpers';

import { ReturnMode, type ReturnModes } from '../../../../enums/return-mode';
import type { ReturnExchange } from '../enhanced-return-types';
import style from './styles.module.less';

export function ReturnDetailsInfo() {
  const { requestDetails } = useReturnDataContext();

  const rmaNumber = requestDetails?.name ?? '';
  const returnMode = requestDetails?.mode ?? ReturnMode.Basic;
  const rmaCreatedDate = requestDetails?.createdOn ?? '';
  const totalAmount = requestDetails?.paymentInfo?.amount ?? 0;
  const shippingFee = requestDetails?.paymentInfo?.shippingFee ?? 0;
  const carrierFee = requestDetails?.shippingInfo?.shippingCost?.amount ?? 0;
  const currencyCode = requestDetails?.paymentInfo?.currency ?? '';
  const returnProcessingFee = requestDetails?.paymentInfo?.returnProcessingFee ?? 0;
  const paymentProcessingFee = requestDetails?.paymentInfo?.paymentProcessingFee ?? 0;
  const giftCardIncentive = requestDetails?.paymentInfo?.giftCardIncentive ?? 0;

  const isShipmentNeeded = isAwaitingShipment(requestDetails);
  const getCost = costFactory(returnMode);

  const { returnCost, purchaseCost, returnItems, purchaseItems } = useMemo(() => {
    const returnExchanges = requestDetails?.returnExchanges ?? [];
    return returnExchanges.reduce(
      (
        acc: {
          returnCost: number;
          purchaseCost: number;
          returnItems: ReturnExchange[];
          purchaseItems: ReturnExchange[];
        },
        item: ReturnExchange,
      ) => {
        const amount = item?.amount ?? 0;
        const type = item?.entityType ?? '';
        const isPurchase = type === 'PCH';
        const costKey = isPurchase ? 'purchaseCost' : 'returnCost';
        const itemsKey = isPurchase ? 'purchaseItems' : 'returnItems';
        return {
          ...acc,
          [costKey]: acc[costKey] + amount,
          [itemsKey]: [...acc[itemsKey], item],
        };
      },
      { returnCost: 0, purchaseCost: 0, returnItems: [], purchaseItems: [] },
    );
  }, [requestDetails?.returnExchanges]);

  return (
    <>
      <ReturnDetails
        address={<CustomerAddress isEditable={isShipmentNeeded} />}
        costSummary={
          <CostSummary
            carrierFee={carrierFee}
            currencyCode={currencyCode}
            giftCardIncentive={getCost(giftCardIncentive)}
            mode={returnMode}
            paymentProcessingFee={getCost(paymentProcessingFee)}
            purchaseCost={getCost(purchaseCost)}
            returnCost={getCost(returnCost)}
            returnProcessingFee={getCost(returnProcessingFee)}
            shippingFee={getCost(shippingFee)}
            totalAmount={getCost(totalAmount)}
          />
        }
        rmaInfo={<RmaInfo createdDate={rmaCreatedDate} rmaNumber={rmaNumber} />}
      />
      <LineItems
        itemDataGenerator={(item) => ({
          name: item?.orderedVariant?.variantName ?? '',
          sku: item?.orderedVariant?.sku ?? '',
          imageURL: item?.orderedVariant?.imageUrl ?? '',
          quantity: item?.quantity ?? 0,
          totalPrice: (item?.amount ?? 0) * -1,
          currency: item?.currency ?? '',
          returnReason: item?.requestReason ?? '',
        })}
        items={returnItems}
        renderItemSubRow={(item) => {
          const data = item?.exchangeVariant ?? null;
          return (
            <FlexColumn className={style['item-content']} gap={Gap.Space16}>
              <ExchangeItem data={data} />
              <LineItemActions item={item} />
            </FlexColumn>
          );
        }}
      />
      <LineItems
        itemDataGenerator={(item) => ({
          name: item?.purchaseVariant?.variantName ?? '',
          sku: item?.purchaseVariant?.sku ?? '',
          imageURL: item?.purchaseVariant?.imageUrl ?? '',
          quantity: item?.quantity ?? 0,
          totalPrice: (item?.amount ?? 0) * -1,
          currency: item?.currency ?? '',
          returnReason: item?.requestReason ?? '',
        })}
        items={purchaseItems}
        type='Purchase'
      />
    </>
  );
}

const costFactory = (returnMode: ReturnModes) => (amount: number) => {
  const isAdvancedMode = returnMode === 'ADVANCED';
  return isAdvancedMode ? amount : 0;
};
